import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { getErrorMessage } from '../types/api-error';
import type { Store } from '../types/admin';

export function StoresPage() {
  const [stores, setStores] = useState<Store[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStores = async (search?: string) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get<Store[]>('/stores', {
        params: search ? { q: search.trim() } : {},
      });
      setStores(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getErrorMessage(err, '加载门店失败'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchStores(q);
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">门店管理</h2>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="搜索门店名称"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-0 px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-base"
        />
        <button
          type="submit"
          className="px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 active:bg-indigo-800 whitespace-nowrap"
        >
          搜索
        </button>
        <button
          type="button"
          onClick={() => {
            setQ('');
            fetchStores();
          }}
          className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 whitespace-nowrap"
        >
          重置
        </button>
      </form>

      {error && (
        <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">加载中…</div>
      ) : stores.length === 0 ? (
        <div className="text-center py-10 text-gray-500">未找到门店</div>
      ) : (
        <>
          <div className="hidden sm:block bg-white rounded-xl shadow overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">门店名称</th>
                  <th className="px-4 py-3 text-left font-medium">创建时间</th>
                  <th className="px-4 py-3 text-right font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stores.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{s.storeName || '-'}</td>
                    <td className="px-4 py-3">{s.createdAt ? new Date(s.createdAt).toLocaleString() : '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/stores/${s.id}/members`}
                        className="inline-flex items-center px-3 py-1.5 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium hover:bg-indigo-100 active:bg-indigo-200"
                      >
                        成员管理
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-3">
            {stores.map((s) => (
              <div key={s.id} className="bg-white rounded-xl shadow p-4">
                <div className="font-medium text-gray-900 mb-2">{s.storeName || '-'}</div>
                <div className="text-sm text-gray-600 mb-3">
                  创建时间：{s.createdAt ? new Date(s.createdAt).toLocaleString() : '-'}
                </div>
                <Link
                  to={`/stores/${s.id}/members`}
                  className="block w-full text-center px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 active:bg-indigo-800"
                >
                  成员管理
                </Link>
              </div>
            ))}
          </div>
        </>
      )}
    </Layout>
  );
}
